<script setup>
import TenantAppointmentActionsCell from "@frontJs/components/tables/cells/projectManagement/apikom/tenantReports/TenantAppointmentActionsCell.vue";
import TenantAppointmentCell from "@frontJs/components/tables/cells/projectManagement/apikom/tenantReports/TenantAppointmentCell.vue";
import { PlusIcon } from "@heroicons/vue/24/solid";
import ActionButton from "@js/components/ActionButton.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import DangerIconCell from "@js/components/tables/cells/DangerIconCell.vue";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatDate } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";
import PillCell from "@js/components/tables/cells/PillCell.vue";

const { t } = useI18n();

const { userSessionKey, forthcoming, makeAnAppointment } = defineProps({
  userSessionKey: {
    type: String,
    required: false,
  },
  forthcoming: {
    type: Boolean,
    required: false,
    default: false,
  },
  makeAnAppointment: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  updateData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href: route("front.projectManagement.apikom.appointments", {
            tenant: props.row.original.id,
          }),
          inertia: true,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "lastLoginDate",
      header: () => t("label.lastLoginDate"),
      cell: (props) =>
        props.row.original.lastLoginDate
          ? h(TextCellV2, {
              text: formatDate(props.row.original.lastLoginDate, false, true),
            })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "appointmentFrequency",
      header: () => t("label.appointmentFrequency"),
      cell: (props) =>
        props.row.original.appointmentFrequency
          ? h(PillCell, {
            pillValue: t("label." + props.row.original.appointmentFrequency),
          })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "lastContactAppointment",
      header: () => t("label.lastAppointment"),
      cell: (props) =>
        props.row.original.lastContactAppointment
          ? h(TenantAppointmentCell, {
              appointment: props.row.original.lastContactAppointment,
            })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "nextAppointment",
      header: () => t("label.nextAppointment"),
      cell: (props) =>
        props.row.original.nextAppointment
          ? h(TenantAppointmentCell, {
              appointment: props.row.original.nextAppointment,
            })
          : null,
    }),
    columnHelper.accessor(() => null, {
      id: "appointmentMailRequestSentAt",
      header: () => t("label.email"),
      cell: (props) =>
        props.row.original.appointmentMailRequestSentAt
          ? h(TextCellV2, {
              text: formatDate(props.row.original.appointmentMailRequestSentAt),
            })
          : null,
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      headerClasses: "justify-center",
      cell: (innerProps) =>
        h(TenantAppointmentActionsCell, {
          row: innerProps.row,
          callback: updateData,
        }),
      width: 0,
    }),
  ],
  url: route("front.projectManagement.apikom.postAppointments"),
  data: {
    forthcoming,
    makeAnAppointment,
  },
  userSessionKey,
  columnPinning: {
    right: ["actions"],
  },
  columnVisibility: {
    nextAppointment: makeAnAppointment !== true,
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
  >
    <template #actions>
      <ActionButton
        size="small"
        :href="route('front.projectManagement.apikom.createAppointment')"
        :left-icon="PlusIcon"
      >
        {{ t("button.add") }}
      </ActionButton>
    </template>
  </DataTableLayout>
</template>
